import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { MapBox, MapDef, MapTerrain } from "@rietz/shared";
import { terrainQuadIsHole } from "@rietz/shared";
import {
  AO_BOTTOM,
  AO_SIDE,
  AO_SIDE_VARIATION,
  AO_TOP,
  MATERIAL_COLORS,
  MATERIAL_JITTER,
  SNOW_GROUND_COLOR,
  SNOW_GROUND_MIX,
  SNOW_ROOF_COLOR,
  SNOW_ROOF_MIX,
  groundDarkening,
  jitterColor,
  randSym,
} from "./palette.js";
import { SHADOWS_ENABLED } from "./shadows.js";

/**
 * Static map geometry: the terrain heightfield and `map.solids`, welded into
 * ONE `BufferGeometry` with ONE `MeshLambertMaterial` — a 2000-box village at
 * one draw call. All per-box identity lives in vertex colours: the material's
 * tone nudged by a hash of the box index, baked AO (tops full, sides ~0.85 with
 * a per-face wobble, undersides 0.62), and a darkening gradient over the lowest
 * half-metre of every vertical face as a contact shadow. `colorTint` multiplies
 * in on top.
 *
 * SNOW is baked in here — terrain and roof faces lerped towards white as the
 * vertices are written (`SNOW_GROUND_MIX`, `SNOW_ROOF_MIX`) — so the coat is
 * decided ONCE, at build time (`WorldView.setEnv`).
 */

export interface BuiltMapMesh {
  group: THREE.Group;
  /** Triangles in the merged mesh — reported by the render smoke test. */
  triangles: number;
  /** Merged solids always draw in one call; 0 when the map has no solids. */
  drawCalls: number;
  dispose(): void;
}

/** Scratch — `buildMapMesh` runs off the hot path but allocation is still rude. */
const baseColor = new THREE.Color();
const tintColor = new THREE.Color();
const outColor = new THREE.Color();
const snowColor = new THREE.Color();

/** What a map build needs to know beyond the map itself. */
export interface MapMeshOptions {
  /**
   * Lay snow on the ground and every upward-facing solid face. Passed by
   * `WorldView.loadMap` from the raid's weather; off for every other caller.
   */
  snow?: boolean;
}

export function buildMapMesh(map: MapDef, opts: MapMeshOptions = {}): BuiltMapMesh {
  const snow = opts.snow === true;
  const group = new THREE.Group();
  group.name = `solids:${map.id}`;

  const parts: THREE.BufferGeometry[] = [];
  if (map.terrain !== undefined) parts.push(terrainGeometryFor(map.terrain, snow));
  for (let i = 0; i < map.solids.length; i++) {
    const solid = map.solids[i];
    if (solid === undefined) continue;
    const geo = boxGeometryFor(solid, i, snow);
    if (geo !== null) parts.push(geo);
  }

  if (parts.length === 0) {
    return { group, triangles: 0, drawCalls: 0, dispose: () => {} };
  }

  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (merged === null) {
    // Only reachable if the per-box attribute sets ever diverge.
    throw new Error("buildMapMesh: geometry merge failed");
  }
  merged.computeBoundingSphere();

  const material = new THREE.MeshLambertMaterial({
    vertexColors: true,
    // NOT flat-shaded, although the look is flat: `flatShading` rebuilds
    // normals
    // from screen-space derivatives, which turn to noise once far terrain
    // triangles shrink below a pixel — the green/purple mottle at the horizon.
    // The geometry's own normals give the same picture without it. Dithering
    // stops the shallow fog gradient banding, as on the sky dome.
    dithering: true,
  });

  const mesh = new THREE.Mesh(merged, material);
  mesh.name = "solids";
  // The player is always inside the map's bounds, and the merged mesh spans
  // all of them: culling it can only ever cost time.
  mesh.frustumCulled = false;
  // Under `?shadows=1` only. Set at build time: a map change rebuilds this
  // mesh.
  // `frustumCulled = false` also means the whole map is re-submitted for the
  // shadow pass every frame — the honest cost on a merged mesh.
  if (SHADOWS_ENABLED) {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  }
  group.add(mesh);

  const index = merged.getIndex();
  const triangles =
    (index !== null ? index.count : merged.attributes.position?.count ?? 0) / 3;

  return {
    group,
    triangles,
    drawCalls: 1,
    dispose(): void {
      group.remove(mesh);
      merged.dispose();
      material.dispose();
    },
  };
}

// ---------------------------------------------------------------------------

/** One indexed mesh for the entire heightfield (two triangles per grid cell). */
function terrainGeometryFor(terrain: MapTerrain, snow: boolean): THREE.BufferGeometry {
  const count = terrain.width * terrain.depth;
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const nx = terrain.width - 1;
  const nz = terrain.depth - 1;

  for (let j = 0; j < terrain.depth; j++) {
    for (let i = 0; i < terrain.width; i++) {
      const k = j * terrain.width + i;
      const o = k * 3;
      positions[o] = terrain.origin.x + i * terrain.cellSize;
      positions[o + 1] = terrain.heights[k] as number;
      positions[o + 2] = terrain.origin.z + j * terrain.cellSize;

      // Central differences give smooth, stable normals without expanding the
      // shared vertex grid or invoking a large temporary normal accumulator.
      const il = Math.max(0, i - 1);
      const ir = Math.min(terrain.width - 1, i + 1);
      const jt = Math.max(0, j - 1);
      const jb = Math.min(terrain.depth - 1, j + 1);
      const dx =
        ((terrain.heights[j * terrain.width + ir] as number) -
          (terrain.heights[j * terrain.width + il] as number)) /
        Math.max(terrain.cellSize, (ir - il) * terrain.cellSize);
      const dz =
        ((terrain.heights[jb * terrain.width + i] as number) -
          (terrain.heights[jt * terrain.width + i] as number)) /
        Math.max(terrain.cellSize, (jb - jt) * terrain.cellSize);
      const inv = 1 / Math.hypot(dx, 1, dz);
      normals[o] = -dx * inv;
      normals[o + 1] = inv;
      normals[o + 2] = -dz * inv;

      const ci = Math.min(nx - 1, i);
      const cj = Math.min(nz - 1, j);
      const sid = terrain.surfaceIds[cj * nx + ci] as number;
      const surface = terrain.surfaces[sid];
      const material = surface?.material ?? "dirt";
      tintColor.setHex(surface?.colorTint ?? MATERIAL_COLORS[material]);
      jitterColor(outColor, tintColor, MATERIAL_JITTER[material], sid, 0x71e2);
      // AFTER the jitter, never instead of it: the wobble is the only texture
      // this ground has.
      if (snow) outColor.lerp(snowColor.setHex(SNOW_GROUND_COLOR), SNOW_GROUND_MIX[material]);
      colors[o] = outColor.r;
      colors[o + 1] = outColor.g;
      colors[o + 2] = outColor.b;
    }
  }

  // Hole quads contribute no triangles; the building slab above the cellar
  // covers the gap. The vertex grid stays complete.
  const indices = new Uint32Array(nx * nz * 6);
  let p = 0;
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      if (terrainQuadIsHole(terrain, i, j)) continue;
      const a = j * terrain.width + i;
      const b = a + 1;
      const c = a + terrain.width;
      const d = c + 1;
      indices[p++] = a; indices[p++] = c; indices[p++] = b;
      indices[p++] = b; indices[p++] = c; indices[p++] = d;
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.setIndex(new THREE.BufferAttribute(indices.subarray(0, p), 1));
  return geo;
}

// ---------------------------------------------------------------------------

/**
 * One box in world space with its vertex colours baked in; `null` for a
 * degenerate box.
 */
function boxGeometryFor(
  solid: MapBox,
  index: number,
  snow: boolean,
): THREE.BufferGeometry | null {
  const { min, max } = solid.box;
  const sx = max.x - min.x;
  const sy = max.y - min.y;
  const sz = max.z - min.z;
  if (!(sx > 0) || !(sy > 0) || !(sz > 0)) return null;

  const cx = (min.x + max.x) * 0.5;
  const cy = (min.y + max.y) * 0.5;
  const cz = (min.z + max.z) * 0.5;

  const geo = new THREE.BoxGeometry(sx, sy, sz);
  // No textures this phase; dropping UVs shaves a third off the merged buffer.
  geo.deleteAttribute("uv");

  // `colorTint` wins the hue; the material still decides how much this surface
  // may wobble.
  const material = solid.material;
  if (solid.colorTint !== undefined) tintColor.setHex(solid.colorTint);
  else tintColor.setHex(MATERIAL_COLORS[material]);
  baseColor.copy(tintColor);
  jitterColor(outColor, baseColor, MATERIAL_JITTER[material], index, 0x5eed);

  const pos = geo.attributes.position as THREE.BufferAttribute;
  const nrm = geo.attributes.normal as THREE.BufferAttribute;
  const count = pos.count;
  const colors = new Float32Array(count * 3);

  // The top faces' colour, worked out ONCE — and only the tops: snow does not
  // stick to a wall.
  if (snow) {
    snowColor.copy(outColor).lerp(tintColor.setHex(SNOW_ROOF_COLOR), SNOW_ROOF_MIX);
  }

  for (let i = 0; i < count; i++) {
    const ny = nrm.getY(i);
    const c = snow && ny > 0.5 ? snowColor : outColor;
    let f: number;
    if (ny > 0.5) {
      f = AO_TOP;
    } else if (ny < -0.5) {
      f = AO_BOTTOM;
    } else {
      // BoxGeometry lays out four vertices per face, so `i >> 2` is the face.
      const face = i >> 2;
      f =
        (AO_SIDE + randSym(index, 0x4f00 + face) * AO_SIDE_VARIATION) *
        groundDarkening(cy + pos.getY(i));
    }
    const o = i * 3;
    colors[o] = c.r * f;
    colors[o + 1] = c.g * f;
    colors[o + 2] = c.b * f;
  }

  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.translate(cx, cy, cz);
  return geo;
}
